import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../config/api';
import { useFeatures } from '../../hooks/useFeatures';

const formatDateLocal = (y, m, d) =>
  `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

const todayLocal = () => {
  const now = new Date();
  return formatDateLocal(now.getFullYear(), now.getMonth()+1, now.getDate());
};

const PERIODS = [
  { label: 'Today', getValue: () => { const d = todayLocal(); return { from: d, to: d }; }},
  { label: 'This Week', getValue: () => {
    const now = new Date(); const day = now.getDay(); const diff = day === 0 ? -6 : 1 - day;
    const mon = new Date(now); mon.setDate(now.getDate() + diff);
    return { from: formatDateLocal(mon.getFullYear(), mon.getMonth()+1, mon.getDate()), to: todayLocal() };
  }},
  { label: 'This Month', getValue: () => { const now = new Date(); return { from: formatDateLocal(now.getFullYear(), now.getMonth()+1, 1), to: todayLocal() }; }},
  { label: 'This Year', getValue: () => { const now = new Date(); return { from: formatDateLocal(now.getFullYear(), 1, 1), to: todayLocal() }; }},
  { label: 'Custom', getValue: () => ({ from: '', to: '' }) },
];

export default function CashFlow() {
  const [activePeriod, setActivePeriod] = useState('This Month');
  const [dateFrom, setDateFrom] = useState(() => { const now = new Date(); return formatDateLocal(now.getFullYear(), now.getMonth()+1, 1); });
  const [dateTo, setDateTo] = useState(() => todayLocal());
  const [expandedMethod, setExpandedMethod] = useState(null);
  const features = useFeatures();

  const handlePeriod = (p) => {
    setActivePeriod(p.label);
    if (p.label !== 'Custom') { const { from, to } = p.getValue(); setDateFrom(from); setDateTo(to); }
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['cash-flow', dateFrom, dateTo],
    queryFn: () => api.get('/api/payments/summary', { params: { ...(dateFrom && { date_from: dateFrom }), ...(dateTo && { date_to: dateTo }) } }).then(r => r.data),
  });

  const summary      = data?.summary      || [];
  const totals       = data?.totals       || { in: 0, out: 0, net: 0 };
  const transactions = data?.transactions || [];
  const byCurrency   = data?.by_currency  || [];
  const dualCurrency = !!features.second_currency;

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const fmtTime = (d) => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  // show each amount in its OWN currency (raw — never converted)
  const fmtMoney = (amount, currency) => currency === 'LBP'
    ? `${Math.round(Number(amount) || 0).toLocaleString('en-US')} LBP`
    : `$${(Number(amount) || 0).toFixed(2)}`;

  const sourceTag = (t) => t.source === 'expense'
    ? <span className="px-1.5 py-0.5 bg-orange-100 text-orange-600 rounded text-xs font-medium">Expense</span>
    : <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-xs font-medium">Payment</span>;

  return (
    <div className="p-4 md:p-6 text-gray-800">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Cash Flow</h1>
          <p className="text-gray-500 text-xs md:text-sm mt-0.5">{dualCurrency ? 'Top cards ≈ USD · real currency split below' : 'Money in and out by payment method'}</p>
        </div>
      </div>

      {/* period filter */}
      <div className="bg-white border border-gray-200 rounded-xl p-3 md:p-4 mb-4 flex flex-wrap gap-2 items-center">
        <div className="flex gap-1 flex-wrap">
          {PERIODS.map(p => (
            <button key={p.label} onClick={() => handlePeriod(p)}
              className={`px-2.5 py-1.5 rounded-lg text-xs md:text-sm font-medium transition-colors ${activePeriod === p.label ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
              {p.label}
            </button>
          ))}
        </div>
        {activePeriod === 'Custom' && (
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
            <span className="text-gray-400 text-xs">to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" />
          </div>
        )}
        {activePeriod !== 'Custom' && dateFrom && (
          <span className="text-xs text-gray-400">{fmtDate(dateFrom)} — {fmtDate(dateTo)}</span>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-600 text-center">Failed to load data</div>
      ) : (
        <>
          {/* ── 3 summary cards — responsive text ── */}
          <div className="grid grid-cols-3 gap-2 md:gap-4 mb-4">
            <div className="bg-white rounded-xl border border-green-200 p-3 md:p-4 shadow-sm">
              <p className="text-xs text-green-600 mb-1">Total In</p>
              <p className="text-base md:text-2xl font-bold text-green-600">${totals.in.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1 hidden md:block">Money received</p>
            </div>
            <div className="bg-white rounded-xl border border-red-200 p-3 md:p-4 shadow-sm">
              <p className="text-xs text-red-500 mb-1">Total Out</p>
              <p className="text-base md:text-2xl font-bold text-red-500">${totals.out.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1 hidden md:block">Payments + expenses</p>
            </div>
            <div className={`bg-white rounded-xl border p-3 md:p-4 shadow-sm ${totals.net >= 0 ? 'border-blue-200' : 'border-orange-200'}`}>
              <p className={`text-xs mb-1 ${totals.net >= 0 ? 'text-blue-600' : 'text-orange-500'}`}>Net</p>
              <p className={`text-base md:text-2xl font-bold ${totals.net >= 0 ? 'text-blue-600' : 'text-orange-500'}`}>${totals.net.toFixed(2)}</p>
              <p className="text-xs text-gray-400 mt-1 hidden md:block">In − Out</p>
            </div>
          </div>

          {/* ── Per-currency split (raw, never converted) ── */}
          {dualCurrency && byCurrency.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 mb-4">
              {byCurrency.map(c => (
                <div key={c.currency} className="bg-white rounded-xl border border-gray-200 p-3 md:p-4 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-800">{c.currency}</span>
                    <span className={`text-sm font-bold ${c.net >= 0 ? 'text-blue-600' : 'text-orange-500'}`}>{fmtMoney(c.net, c.currency)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-600">In&nbsp;{fmtMoney(c.in, c.currency)}</span>
                    <span className="text-red-500">Out&nbsp;{fmtMoney(c.out, c.currency)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── By Payment Method — horizontal scroll on mobile ── */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-bold text-gray-700">By Payment Method</h2>
            </div>
            {summary.length === 0 ? (
              <p className="text-gray-400 text-center py-8">No transactions found for this period.</p>
            ) : (
              <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
                <table className="w-full text-sm" style={{minWidth:500}}>
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Method</th>
                      <th className="text-right px-4 py-3 font-semibold text-green-600">In</th>
                      <th className="text-right px-4 py-3 font-semibold text-red-500">Out</th>
                      <th className="text-right px-4 py-3 font-semibold">Net</th>
                      <th className="text-right px-4 py-3 font-semibold">Tx</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {summary.map(m => (
                      <>
                        <tr key={m.method} className="hover:bg-gray-50/50 cursor-pointer"
                          onClick={() => setExpandedMethod(expandedMethod === m.method ? null : m.method)}>
                          <td className="px-4 py-3 font-semibold text-gray-800">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0"></div>
                              {m.method}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-green-600">${m.in.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right font-medium text-red-500">${m.out.toFixed(2)}</td>
                          <td className={`px-4 py-3 text-right font-bold ${m.net >= 0 ? 'text-blue-600' : 'text-orange-500'}`}>${m.net.toFixed(2)}</td>
                          <td className="px-4 py-3 text-right text-gray-400">{m.transactions.length}</td>
                          <td className="px-4 py-3 text-right text-gray-400 text-xs">{expandedMethod === m.method ? '▲' : '▼'}</td>
                        </tr>
                        {expandedMethod === m.method && m.transactions.map((t, idx) => (
                          <tr key={idx} className={`text-xs ${t.source === 'expense' ? 'bg-orange-50/30' : 'bg-gray-50/30'}`}>
                            <td className="px-4 py-2 pl-8 text-gray-500 whitespace-nowrap">
                              {fmtDate(t.date)} {fmtTime(t.date)}
                              <span className="ml-2">{sourceTag(t)}</span>
                            </td>
                            <td className="px-4 py-2 text-right">{t.direction === 'in' ? <span className="text-green-600 font-medium">{fmtMoney(t.amount, t.currency)}</span> : <span className="text-gray-300">—</span>}</td>
                            <td className="px-4 py-2 text-right">{t.direction === 'out' ? <span className="text-red-500 font-medium">{fmtMoney(t.amount, t.currency)}</span> : <span className="text-gray-300">—</span>}</td>
                            <td className="px-4 py-2 text-right text-gray-500">{t.description}</td>
                            <td className="px-4 py-2 text-right font-mono text-blue-500">{t.reference}</td>
                            <td></td>
                          </tr>
                        ))}
                      </>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t-2 border-gray-200 text-sm font-bold">
                    <tr>
                      <td className="px-4 py-3 text-gray-700">Total</td>
                      <td className="px-4 py-3 text-right text-green-600">${totals.in.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-red-500">${totals.out.toFixed(2)}</td>
                      <td className={`px-4 py-3 text-right ${totals.net >= 0 ? 'text-blue-600' : 'text-orange-500'}`}>${totals.net.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right text-gray-400">{transactions.length}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* ── All Transactions — horizontal scroll on mobile ── */}
          {transactions.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="text-sm font-bold text-gray-700">All Transactions ({transactions.length})</h2>
              </div>
              <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
                <table className="w-full text-sm" style={{minWidth:600}}>
                  <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">Date</th>
                      <th className="text-left px-4 py-3 font-semibold">Source</th>
                      <th className="text-left px-4 py-3 font-semibold">Description</th>
                      <th className="text-left px-4 py-3 font-semibold">Method</th>
                      <th className="text-left px-4 py-3 font-semibold">Notes</th>
                      <th className="text-right px-4 py-3 font-semibold text-green-600">In</th>
                      <th className="text-right px-4 py-3 font-semibold text-red-500">Out</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {transactions.map((t, idx) => (
                      <tr key={idx} className={`hover:bg-gray-50/50 ${t.source === 'expense' ? 'bg-orange-50/10' : t.direction === 'in' ? 'bg-green-50/10' : 'bg-red-50/10'}`}>
                        <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(t.date)} {fmtTime(t.date)}</td>
                        <td className="px-4 py-2.5">{sourceTag(t)}</td>
                        <td className="px-4 py-2.5 text-gray-700">{t.description}{t.detail && <span className="text-gray-400 ml-1 text-xs">— {t.detail}</span>}</td>
                        <td className="px-4 py-2.5 text-gray-600 font-medium whitespace-nowrap">{t.method}</td>
                        <td className="px-4 py-2.5 text-gray-400 text-xs">{t.notes || '—'}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-green-600">{t.direction === 'in' ? fmtMoney(t.amount, t.currency) : '—'}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-red-500">{t.direction === 'out' ? fmtMoney(t.amount, t.currency) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}